import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Map, TileLayer, CircleMarker, Popup, Marker, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { baseUrl } from '../../config';
import { Spin, Radio, Tag, Select, Alert } from 'antd';
import { GlobalOutlined, HomeOutlined, LoadingOutlined } from '@ant-design/icons';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ── All 50 Rajasthan district centres ────────────────────────────────────────
const RJ_DISTRICTS = {
    'Ajmer':             [26.4499, 74.6399],
    'Alwar':             [27.5530, 76.6346],
    'Banswara':          [23.5440, 74.4424],
    'Baran':             [25.1026, 76.5130],
    'Barmer':            [25.7500, 71.3949],
    'Bharatpur':         [27.2152, 77.4900],
    'Bhilwara':          [25.3407, 74.6313],
    'Bikaner':           [28.0229, 73.3119],
    'Bundi':             [25.4391, 75.6444],
    'Chittorgarh':       [24.8887, 74.6269],
    'Churu':             [28.2980, 74.9669],
    'Dausa':             [26.8836, 76.3340],
    'Dholpur':           [26.7027, 77.8940],
    'Dungarpur':         [23.8437, 73.7148],
    'Hanumangarh':       [29.5800, 74.3273],
    'Jaipur':            [26.9124, 75.7873],
    'Jaisalmer':         [26.9157, 70.9083],
    'Jalore':            [25.3450, 72.6148],
    'Jhalawar':          [24.5979, 76.1577],
    'Jhunjhunu':         [28.1290, 75.3989],
    'Jodhpur':           [26.2389, 73.0243],
    'Karauli':           [26.5023, 77.0191],
    'Kota':              [25.2138, 75.8648],
    'Nagaur':            [27.2016, 73.7368],
    'Pali':              [25.7781, 73.3234],
    'Pratapgarh':        [24.0322, 74.7784],
    'Rajsamand':         [25.0754, 73.8830],
    'Sawai Madhopur':    [25.9951, 76.3568],
    'Sikar':             [27.6094, 75.1399],
    'Sirohi':            [24.8864, 72.8636],
    'Sri Ganganagar':    [29.9091, 73.8800],
    'Tonk':              [26.1659, 75.7897],
    'Udaipur':           [24.5854, 73.7125],
    // New 2023 districts
    'Anupgarh':          [29.1985, 73.2059],
    'Balotra':           [25.8316, 72.2403],
    'Beawar':            [26.1017, 74.3175],
    'Deeg':              [27.4716, 77.2008],
    'Didwana-Kuchaman':  [27.3981, 74.5680],
    'Dudu':              [26.8330, 75.3998],
    'Gangapur City':     [26.4695, 76.7140],
    'Jaipur Rural':      [26.9800, 75.5500],
    'Jodhpur Rural':     [26.3500, 72.6500],
    'Kekri':             [25.9738, 75.1536],
    'Khairthal-Tijara':  [27.8298, 76.6395],
    'Kotputli-Behror':   [27.7059, 76.1980],
    'Neem Ka Thana':     [27.7360, 75.7918],
    'Phalodi':           [27.1339, 72.3682],
    'Salumbar':          [24.1311, 74.0492],
    'Sanchore':          [24.7537, 71.7777],
    'Shahpura':          [27.3906, 75.9551],
};

const INDIA_STATES = {
    'Rajasthan':        [27.0238, 74.2179],
    'Gujarat':          [22.2587, 71.1924],
    'Maharashtra':      [19.7515, 75.7139],
    'Delhi':            [28.6139, 77.2090],
    'Uttar Pradesh':    [26.8467, 80.9462],
    'Madhya Pradesh':   [22.9734, 78.6569],
    'Haryana':          [29.0588, 76.0856],
    'Punjab':           [31.1471, 75.3412],
    'Karnataka':        [15.3173, 75.7139],
    'Tamil Nadu':       [11.1271, 78.6569],
    'West Bengal':      [22.9868, 87.8550],
    'Andhra Pradesh':   [15.9129, 79.7400],
    'Telangana':        [18.1124, 79.0193],
    'Bihar':            [25.0961, 85.3131],
    'Jharkhand':        [23.6102, 85.2799],
    'Odisha':           [20.9517, 85.0985],
    'Chhattisgarh':     [21.2787, 81.8661],
    'Uttarakhand':      [30.0668, 79.0193],
    'Himachal Pradesh': [31.1048, 77.1734],
    'Jammu & Kashmir':  [33.7782, 76.5762],
    'Assam':            [26.2006, 92.9376],
    'Kerala':           [10.8505, 76.2711],
    'Goa':              [15.2993, 74.1240],
};

const TILES = {
    physical:  { label: '🏔 Physical',  url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',  attribution: '© OpenTopoMap © OSM', maxZoom: 17 },
    road:      { label: '🛣 Road',      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OpenStreetMap',     maxZoom: 19 },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri', maxZoom: 18 },
};

const dotColor = (count, max) => {
    const p = max > 0 ? count / max : 0;
    if (p > 0.6) return '#dc2626';
    if (p > 0.3) return '#f59e0b';
    if (p > 0.1) return '#2563eb';
    return '#6366f1';
};
const dotRadius = (count, max, lo = 8, hi = 44) =>
    max > 0 ? lo + (count / max) * (hi - lo) : lo;

// Custom village pin icon
const villageIcon = (highlighted) => L.divIcon({
    className: '',
    html: `<div style="
        width:${highlighted ? 22 : 14}px; height:${highlighted ? 22 : 14}px;
        background:${highlighted ? '#f59e0b' : '#7c3aed'};
        border:${highlighted ? '3px solid #7a0000' : '2px solid #fff'};
        border-radius:50%; box-shadow:0 2px 8px rgba(0,0,0,0.35);
        transition:all 0.2s;
    "></div>`,
    iconAnchor: [highlighted ? 11 : 7, highlighted ? 11 : 7],
});

function Legend({ max }) {
    return (
        <div style={{
            position: 'absolute', bottom: 28, right: 12, zIndex: 1000,
            background: 'rgba(255,255,255,0.96)', borderRadius: 8,
            padding: '10px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.18)', fontSize: 11,
        }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: '#7a0000', fontSize: 12 }}>Member Density</div>
            {[
                ['#dc2626', `High  (>${Math.round(max * 0.6)})`],
                ['#f59e0b', `Medium`],
                ['#2563eb', `Low`],
                ['#6366f1', `Very low`],
                ['#7c3aed', 'Village pin (exact location)'],
                ['#f59e0b', 'Selected village'],
            ].map(([color, label]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <div style={{ width: 11, height: 11, borderRadius: '50%', background: color, border: '1.5px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                    <span style={{ color: '#374151' }}>{label}</span>
                </div>
            ))}
            <div style={{ marginTop: 6, color: '#94a3b8', fontSize: 10 }}>District bubble size ∝ members</div>
        </div>
    );
}

export default function MapView() {
    const mapRef = useRef(null);
    const [mode, setMode]               = useState('rajasthan');
    const [tileKey, setTileKey]         = useState('physical');
    const [loading, setLoading]         = useState(true);
    const [states, setStates]           = useState([]);
    const [districts, setDistricts]     = useState([]);
    const [villages, setVillages]       = useState([]);
    const [geoJson, setGeoJson]         = useState(null);
    const [selDistrict, setSelDistrict]     = useState(null);
    const [selVillage, setSelVillage]       = useState(null);
    // Batch village pins for selected district
    const [districtPins, setDistrictPins]   = useState([]); // [{lat,lng,village,tehsil,count}]
    const [batchLoading, setBatchLoading]   = useState(false);
    const [batchProgress, setBatchProgress] = useState('');
    // Single village pin from village dropdown
    const [villageCoords, setVillageCoords] = useState(null);
    const [geocoding, setGeocoding]         = useState(false);
    const [geocodeError, setGeocodeError]   = useState(null);

    useEffect(() => {
        const h = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
        axios.get(`${baseUrl}/geo-report/map-data`, { headers: h })
            .then(r => {
                setStates(r.data.result.states    || []);
                setDistricts(r.data.result.districts || []);
                setVillages(r.data.result.villages   || []);
            })
            .catch(console.error)
            .finally(() => setLoading(false));

        fetch('https://raw.githubusercontent.com/datameet/maps/master/Districts/rajasthan.geojson')
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setGeoJson(d); })
            .catch(() => {});
    }, []);

    // Fly on mode change
    useEffect(() => {
        const m = mapRef.current && mapRef.current.leafletElement;
        if (!m) return;
        if (mode === 'rajasthan') m.flyTo([27.0238, 74.2179], 7, { duration: 1 });
        else                      m.flyTo([22.5,    82.0   ], 5, { duration: 1 });
    }, [mode]);

    // Fly to district
    useEffect(() => {
        const m = mapRef.current && mapRef.current.leafletElement;
        if (!m || !selDistrict) return;
        const c = RJ_DISTRICTS[selDistrict];
        if (c) m.flyTo(c, 10, { duration: 1 });
    }, [selDistrict]);

    // Batch geocode all villages for the selected district
    useEffect(() => {
        if (!selDistrict || !villages.length) { setDistrictPins([]); return; }

        const distVillages = villages.filter(v => v.district === selDistrict);
        if (!distVillages.length) { setDistrictPins([]); return; }

        setBatchLoading(true);
        setBatchProgress(`Locating ${Math.min(distVillages.length, 30)} villages on map…`);
        setDistrictPins([]);

        const h = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
        axios.post(`${baseUrl}/geo-report/geocode-batch`, {
            district: selDistrict,
            villages: distVillages.slice(0, 30).map(v => ({
                village: v.village, tehsil: v.tehsil, count: v.count,
            })),
        }, { headers: h, timeout: 120000 })
        .then(r => {
            if (r.data.status) setDistrictPins(r.data.result || []);
        })
        .catch(console.error)
        .finally(() => { setBatchLoading(false); setBatchProgress(''); });
    }, [selDistrict, villages]);

    // Geocode village when selected — calls OpenStreetMap Nominatim via backend
    useEffect(() => {
        if (!selVillage) { setVillageCoords(null); setGeocodeError(null); return; }

        // selVillage is "villageName||tehsilName" composite key
        const [villageName, tehsilName] = selVillage.split('||');
        const v = villages.find(x =>
            x.village.toLowerCase() === (villageName || '').toLowerCase() &&
            (x.tehsil || '').toLowerCase() === (tehsilName || '').toLowerCase()
        ) || villages.find(x => x.village.toLowerCase() === (villageName || '').toLowerCase());
        if (!v) return;

        // Set district from village
        if (v.district) setSelDistrict(v.district);

        setGeocoding(true);
        setGeocodeError(null);
        setVillageCoords(null);

        const h = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
        axios.get(`${baseUrl}/geo-report/geocode`, {
            headers: h,
            params: { village: v.village, tehsil: v.tehsil, district: v.district },
        }).then(r => {
            if (r.data.status) {
                const { lat, lng } = r.data;
                setVillageCoords({ lat, lng, display_name: r.data.display_name, count: v.count, tehsil: v.tehsil });
                const m = mapRef.current && mapRef.current.leafletElement;
                if (m) m.flyTo([lat, lng], 13, { duration: 1.2 });
            } else {
                setGeocodeError(`Could not locate "${v.village}" on map — try a different name`);
                // Fall back: fly to district
                const dc = RJ_DISTRICTS[v.district];
                const m  = mapRef.current && mapRef.current.leafletElement;
                if (m && dc) m.flyTo(dc, 11, { duration: 1 });
            }
        }).catch(() => setGeocodeError('Geocoding failed — check internet connection'))
          .finally(() => setGeocoding(false));
    }, [selVillage]);

    const rajDistricts = useMemo(() => districts.filter(d => d.state === 'Rajasthan'), [districts]);
    const maxRaj   = rajDistricts[0]?.count || 1;
    const maxIndia = states[0]?.count || 1;

    const distCountMap = useMemo(() => {
        const m = {};
        rajDistricts.forEach(d => { m[d.district.toLowerCase()] = d.count; });
        return m;
    }, [rajDistricts]);

    const geoJsonStyle = feat => {
        const name  = (feat.properties.NAME_2 || feat.properties.DISTRICT || '').toLowerCase();
        const count = distCountMap[name] || 0;
        const p     = maxRaj > 0 ? count / maxRaj : 0;
        return {
            fillColor:   p > 0.6 ? '#dc2626' : p > 0.3 ? '#f59e0b' : p > 0.1 ? '#2563eb' : count > 0 ? '#6366f1' : '#d1d5db',
            fillOpacity: count > 0 ? 0.28 : 0.05,
            color: '#1e3a5f', weight: 0.8,
        };
    };

    const onEachFeature = (feat, layer) => {
        const name  = feat.properties.NAME_2 || feat.properties.DISTRICT || '';
        const count = distCountMap[name.toLowerCase()] || 0;
        layer.bindTooltip(`<strong>${name}</strong><br/>${count ? count + ' members' : 'No data'}`, { sticky: true });
        layer.on('click', () => {
            const match = rajDistricts.find(d => d.district.toLowerCase() === name.toLowerCase());
            if (match) { setSelDistrict(match.district); setSelVillage(null); setVillageCoords(null); }
        });
    };

    const districtOptions = useMemo(() =>
        rajDistricts.map(d => ({ value: d.district, label: `${d.district} (${d.count})` }))
    , [rajDistricts]);

    const villageOptions = useMemo(() => {
        const src = selDistrict
            ? villages.filter(v => v.district === selDistrict)
            : villages.slice(0, 300);
        const seen = new Set();
        return src
            .filter(v => {
                const key = `${v.village}|${v.tehsil}`.toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .map(v => ({
                value: `${v.village}||${v.tehsil || ''}`,   // unique composite key
                label: `${v.village}${v.tehsil ? ' · ' + v.tehsil : ''} (${v.count})`,
            }));
    }, [villages, selDistrict]);

    const tile = TILES[tileKey];

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 520 }}>
            <Spin size="large" tip="Loading map data…" />
        </div>
    );

    return (
        <div>
            {/* ── Controls ─────────────────────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                marginBottom: 10, padding: '10px 14px',
                background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0',
            }}>
                <Radio.Group value={mode} onChange={e => { setMode(e.target.value); setSelDistrict(null); setSelVillage(null); setVillageCoords(null); setDistrictPins([]); }} buttonStyle="solid" size="small">
                    <Radio.Button value="rajasthan"><HomeOutlined /> Rajasthan</Radio.Button>
                    <Radio.Button value="india"><GlobalOutlined /> India</Radio.Button>
                </Radio.Group>

                <Radio.Group value={tileKey} onChange={e => setTileKey(e.target.value)} size="small">
                    {Object.entries(TILES).map(([k, t]) => (
                        <Radio.Button key={k} value={k}>{t.label}</Radio.Button>
                    ))}
                </Radio.Group>

                {mode === 'rajasthan' && (
                    <Select showSearch allowClear placeholder="Select District…" size="small" style={{ width: 210 }}
                        value={selDistrict} onChange={v => { setSelDistrict(v || null); setSelVillage(null); setVillageCoords(null); }}
                        filterOption={(i, o) => o.label.toLowerCase().includes(i.toLowerCase())}
                        options={districtOptions}
                    />
                )}

                {mode === 'rajasthan' && (
                    <Select showSearch allowClear
                        placeholder={geocoding ? 'Locating on map…' : 'Select Village…'}
                        size="small" style={{ width: 240 }}
                        value={selVillage}
                        onChange={v => setSelVillage(v || null)}
                        filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
                        options={villageOptions}
                        suffixIcon={geocoding ? <LoadingOutlined style={{ color: '#2563eb' }} spin /> : undefined}
                    />
                )}

                {selDistrict && <Tag color="blue" closable onClose={() => { setSelDistrict(null); setSelVillage(null); setVillageCoords(null); setDistrictPins([]); }}>{selDistrict}</Tag>}
                {selVillage  && <Tag color="purple" closable onClose={() => { setSelVillage(null); setVillageCoords(null); }}>{selVillage.split('||')[0]}</Tag>}

                <div style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>
                    {mode === 'rajasthan' ? `${rajDistricts.length} districts · ${villages.length} villages` : `${states.length} states`}
                </div>
            </div>

            {/* Geocode feedback */}
            {geocoding && (
                <Alert message={`Locating "${selVillage ? selVillage.split('||')[0] : ''}" on OpenStreetMap…`} type="info" showIcon style={{ marginBottom: 8, borderRadius: 8 }} />
            )}
            {geocodeError && (
                <Alert message={geocodeError} type="warning" showIcon closable onClose={() => setGeocodeError(null)} style={{ marginBottom: 8, borderRadius: 8 }} />
            )}
            {batchLoading && (
                <Alert
                    message={<span><LoadingOutlined spin style={{ marginRight: 8 }} />{batchProgress}</span>}
                    description="Fetching real GPS coordinates from OpenStreetMap for each village…"
                    type="info" style={{ marginBottom: 8, borderRadius: 8 }}
                />
            )}
            {!batchLoading && districtPins.length > 0 && !selVillage && (
                <Alert
                    message={<span>📍 Showing <strong>{districtPins.length}</strong> village locations for <strong>{selDistrict}</strong></span>}
                    type="success" style={{ marginBottom: 8, borderRadius: 8 }}
                />
            )}
            {villageCoords && !geocoding && (
                <Alert
                    message={<span>📍 <strong>{selVillage ? selVillage.split('||')[0] : ''}</strong> — exact location from OpenStreetMap</span>}
                    description={<span style={{ fontSize: 11, color: '#64748b' }}>{villageCoords.display_name}</span>}
                    type="success" showIcon style={{ marginBottom: 8, borderRadius: 8 }}
                />
            )}

            {/* ── Map ──────────────────────────────────────────────── */}
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 24px rgba(0,0,0,0.12)', position: 'relative' }}>
                <Map
                    ref={mapRef}
                    center={[27.0238, 74.2179]}
                    zoom={7}
                    style={{ height: 580, width: '100%' }}
                    scrollWheelZoom
                >
                    <TileLayer key={tileKey} url={tile.url} attribution={tile.attribution} maxZoom={tile.maxZoom} />

                    {/* ── Rajasthan ─────────────────────────────── */}
                    {mode === 'rajasthan' && <>

                        {geoJson && (
                            <GeoJSON key="rj-geo" data={geoJson} style={geoJsonStyle} onEachFeature={onEachFeature} />
                        )}

                        {/* District density bubbles */}
                        {rajDistricts.map(d => {
                            const c = RJ_DISTRICTS[d.district];
                            if (!c) return null;
                            const col = dotColor(d.count, maxRaj);
                            const sel = selDistrict === d.district;
                            return (
                                <CircleMarker key={d.district} center={c}
                                    radius={dotRadius(d.count, maxRaj)}
                                    color={sel ? '#7a0000' : col}
                                    fillColor={col} fillOpacity={sel ? 0.75 : 0.5}
                                    weight={sel ? 3 : 2}
                                    onClick={() => { setSelDistrict(sel ? null : d.district); setSelVillage(null); setVillageCoords(null); }}
                                >
                                    <Popup>
                                        <div style={{ minWidth: 210 }}>
                                            <div style={{ fontWeight: 800, fontSize: 14, color: '#7a0000', marginBottom: 4 }}>📍 {d.district}</div>
                                            <div style={{ fontSize: 13, color: '#2563eb', fontWeight: 700, marginBottom: 6 }}>
                                                {Number(d.count).toLocaleString('en-IN')} members
                                            </div>
                                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                                                {((d.count / rajDistricts.reduce((s, x) => s + x.count, 0)) * 100).toFixed(1)}% of Rajasthan total
                                            </div>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#7a0000', marginBottom: 4 }}>Top villages:</div>
                                            {villages.filter(v => v.district === d.district).slice(0, 6).map(v => (
                                                <div key={v.village} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0', borderTop: '1px solid #f0f0f0' }}>
                                                    <span>🏘 {v.village}{v.tehsil ? ` · ${v.tehsil}` : ''}</span>
                                                    <strong style={{ marginLeft: 8, color: '#7c3aed' }}>{v.count}</strong>
                                                </div>
                                            ))}
                                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, fontStyle: 'italic' }}>
                                                Use the Village selector above to pin exact location
                                            </div>
                                        </div>
                                    </Popup>
                                </CircleMarker>
                            );
                        })}

                        {/* District village pins — all geocoded real locations */}
                        {!selVillage && districtPins.map((v, i) => (
                            <Marker
                                key={`dp-${i}`}
                                position={[v.lat, v.lng]}
                                icon={villageIcon(false)}
                            >
                                <Popup>
                                    <div style={{ minWidth: 170 }}>
                                        <div style={{ fontWeight: 800, fontSize: 13, color: '#7a0000', marginBottom: 3 }}>🏘 {v.village}</div>
                                        {v.tehsil && <div style={{ fontSize: 11, color: '#64748b' }}>Tehsil: {v.tehsil}</div>}
                                        <div style={{ fontSize: 13, color: '#7c3aed', fontWeight: 700, marginTop: 4 }}>{v.count} members</div>
                                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>📌 OpenStreetMap location</div>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}

                        {/* Single highlighted village pin from dropdown */}
                        {villageCoords && (
                            <Marker
                                position={[villageCoords.lat, villageCoords.lng]}
                                icon={villageIcon(true)}
                            >
                                <Popup>
                                    <div style={{ minWidth: 190 }}>
                                        <div style={{ fontWeight: 800, fontSize: 14, color: '#7a0000', marginBottom: 4 }}>🏘 {selVillage ? selVillage.split('||')[0] : ''}</div>
                                        {villageCoords.tehsil && <div style={{ fontSize: 11, color: '#64748b' }}>Tehsil: {villageCoords.tehsil}</div>}
                                        {selDistrict && <div style={{ fontSize: 11, color: '#64748b' }}>District: {selDistrict}</div>}
                                        <div style={{ fontSize: 13, color: '#7c3aed', fontWeight: 700, marginTop: 6 }}>{villageCoords.count} members</div>
                                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>📌 Exact location via OpenStreetMap</div>
                                    </div>
                                </Popup>
                            </Marker>
                        )}
                    </>}

                    {/* ── India ─────────────────────────────────── */}
                    {mode === 'india' && states.map(s => {
                        const c = INDIA_STATES[s.state];
                        if (!c) return null;
                        return (
                            <CircleMarker key={s.state} center={c}
                                radius={dotRadius(s.count, maxIndia, 10, 54)}
                                color={dotColor(s.count, maxIndia)} fillColor={dotColor(s.count, maxIndia)} fillOpacity={0.55} weight={2}
                            >
                                <Popup>
                                    <div style={{ minWidth: 160 }}>
                                        <div style={{ fontWeight: 800, fontSize: 14, color: '#7a0000', marginBottom: 4 }}>🗺 {s.state}</div>
                                        <div style={{ fontSize: 13, color: '#2563eb', fontWeight: 700 }}>{Number(s.count).toLocaleString('en-IN')} members</div>
                                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                                            {((s.count / states.reduce((a, x) => a + x.count, 0)) * 100).toFixed(1)}% of total
                                        </div>
                                    </div>
                                </Popup>
                            </CircleMarker>
                        );
                    })}

                    <Legend max={mode === 'rajasthan' ? maxRaj : maxIndia} />
                </Map>
            </div>

            {/* ── Top district chips ────────────────────────── */}
            {mode === 'rajasthan' && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {rajDistricts.slice(0, 10).map((d, i) => (
                        <div key={d.district}
                            onClick={() => { setSelDistrict(selDistrict === d.district ? null : d.district); setSelVillage(null); setVillageCoords(null); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                                background: selDistrict === d.district ? '#eff6ff' : '#fff',
                                border: `1px solid ${selDistrict === d.district ? '#2563eb' : '#e2e8f0'}`,
                                borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600,
                            }}
                        >
                            <span style={{
                                width: 20, height: 20, lineHeight: '20px', borderRadius: '50%',
                                textAlign: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0,
                                background: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : '#e2e8f0',
                                color: i < 3 ? '#fff' : '#64748b',
                            }}>{i + 1}</span>
                            <span style={{ color: '#7a0000' }}>{d.district}</span>
                            <span style={{ color: '#2563eb' }}>{Number(d.count).toLocaleString('en-IN')}</span>
                        </div>
                    ))}
                    {rajDistricts.length > 10 && (
                        <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center' }}>+{rajDistricts.length - 10} more</span>
                    )}
                </div>
            )}
        </div>
    );
}
